package pt.at.oss.kotlin.manual.validator.common

import pt.at.oss.kotlin.manual.enumeration.CustomErrorCodes
import pt.at.oss.kotlin.manual.storage.common.bankaccountinfo.BankAccountInfo
import pt.lightweightform.kotlin.type.validator.ValidationCodes
import pt.lightweightform.kotlin.type.validator.ValidationError

/**
 * Created by Pedro Alfaiate on 14/05/2020.
 */
object BankAccountInfoValidator {
    val bankAccountInfoSequencialValidate = fun(bankAccountInfo: BankAccountInfo?, allowOban: Boolean, isRequiredAndAllowed: Boolean): List<ValidationError> {
        // only nullable if the registration is done by an intermediary
        if (bankAccountInfo == null) {
            return if (!isRequiredAndAllowed) emptyList()
            else listOf(ValidationError(code = ValidationCodes.LF_IS_REQUIRED))
        }
        if (!isRequiredAndAllowed) {
            return listOf(ValidationError(code = CustomErrorCodes.BANK_ACCOUNT_INFO_NOT_ALLOWED.code))
        }

        val errors = mutableListOf<ValidationError>()
        // mutex iban and oban
        validateMutexIbanOban(bankAccountInfo.iban, bankAccountInfo.oban)?.also { errors.add(it) }
        // iban
        // -> required if OBAN is not allowed
        validateRequiredStrIf(bankAccountInfo.iban, !allowOban)?.also { errors.add(it) }
        validateIban(bankAccountInfo.iban)?.also { errors.add(it) }
        FreeTextValidator.validateFreeText(bankAccountInfo.iban)?.also { errors.add(it) }
        // bic
        validateBic(bankAccountInfo.bic)?.also { errors.add(it) }

        // account holder
        validateAccountHolder(bankAccountInfo.accountHolder)?.also { errors.add(it) }

        // accountHolder
        // ->required and maxlength
        // oban
        // ->if present has maxlength
        validateOban(bankAccountInfo.oban, allowOban)?.also { errors.add(it) }
        FreeTextValidator.validateFreeText(bankAccountInfo.oban)?.also { errors.add(it) }

        return errors
    }

    val validateMutexIbanOban = fun(iban: String, oban: String): ValidationError? {
        return if (iban.isEmpty() && oban.isEmpty()) ValidationError(code = CustomErrorCodes.BANK_ACCOUNT_ID_NOT_PROVIDED.code)
        else if (iban.isNotEmpty() && oban.isNotEmpty()) ValidationError(code = CustomErrorCodes.IBAN_OBAN_BOTH_NOT_ALLOWED.code)
        else null
    }

    val validateIban = fun(iban: String): ValidationError? {
        return if (iban.isNotEmpty() && !pt.at.lfat.commonjvm.validator.validateIban(iban)) ValidationError(code = CustomErrorCodes.IBAN_INVALID.code)
        else null
    }

    private val bicPattern = Regex("[A-Z]{6}[A-Z2-9][A-NP-Z0-9]([A-Z0-9]{3})?") // ECOM-OSS-FS-v2.02 Table 120 - GEN-RU-BIC-STRUCT
    val validateBic = fun(bic: String): ValidationError? {
        return if (bic.isNotEmpty() && !bicPattern.matches(bic)) ValidationError(code = CustomErrorCodes.BIC_INVALID.code)
        else null
    }

    private val accountHolderPattern = Regex("[A-Za-z0-9][A-Za-z0-9/\\-?:().,'+]*( [A-Za-z0-9/\\-?:().,'+]+)*")
    val validateAccountHolder = fun(accountHolder: String): ValidationError? {
        return if (accountHolder.isNotEmpty() && !accountHolderPattern.matches(accountHolder)) ValidationError(code = CustomErrorCodes.ACCOUNT_HOLDER_INVALID.code)
        else null
    }

    val validateOban = fun(oban: String, allowOban: Boolean): ValidationError? {
        return if (!allowOban && oban.isNotEmpty()) ValidationError(code = CustomErrorCodes.OBAN_NOT_ALLOWED.code)
        else if (oban.isNotEmpty() && oban.isBlank()) ValidationError(code = ValidationCodes.LF_IS_REQUIRED)
        else validateCollapsedSpace(oban)
    }
}
